import type { Placement } from '@floating-ui/react';
import { faCaretDown, faCaretUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
  ComponentPropsWithRef,
  CSSProperties,
  FC,
  ReactNode,
} from 'react';
import { Children, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { isFragment } from 'react-is';
import type { ActionMode } from 'src/components/action-mode';
import { ActionModeProvider, useActionMode } from 'src/components/action-mode';
import type { ModeButtonProps } from 'src/components/buttons-and-actions/button';
import {
  Button,
  mapActionModeToButton,
} from 'src/components/buttons-and-actions/button';
import { useDropdown } from 'src/hooks/use-dropdown';
import { flyoutClasses } from 'src/styles';
import { cn } from 'src/utils/cn';

export const dropdownContentClasses = cn(
  flyoutClasses,
  `relative grid w-max min-w-[17.9rem]`
);

type RenderTriggerProps = {
  buttonProps: ComponentPropsWithRef<typeof Button> & {
    'data-testid'?: string;
  };
};

/**
 * Define the label like this:
 * - non-visual label: `aria-label="Your text..."`
 * - visual label (text only): `label="Your text..."`
 * - icon with a non-visual label: `label={<FontAwesomeIcon icon={icon} aria-label="Your text..." />}`
 * - icon with a non-visual label: `label={<><FontAwesomeIcon icon={icon} /> Your text...</>}`
 */
type DropdownTriggerLabel =
  | {
      /**
       * This allows you to render a custom trigger.
       * Be careful with this setting: we don't want every dropdown to look
       * different!
       */
      renderTrigger?: (props: RenderTriggerProps) => ReactNode;
      label?: undefined;
      'aria-label'?: undefined;
    }
  | {
      renderTrigger?: undefined;
      label: ReactNode;
      'aria-label'?: undefined;
    }
  | {
      renderTrigger?: undefined;
      'aria-label': string;
      label?: undefined;
    };

type DropdownContentContainerProps = {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
};

type DropdownTriggerProps = DropdownTriggerLabel & {
  initialActive?: boolean;
  /**
   * Usually the dropdown manages the active state internally, but this
   * prop allows you to control it externally.
   * If this is set it will also be used for `initialActive`, if it wasn't
   * specified separately.
   */
  active?: boolean;
  'data-testid'?: string;
  /**
   * Passed to the trigger button.
   */
  className?: string;
  children: ReactNode;
  /**
   * If provided, the children will be wrapped in this container.
   * Be careful with this setting: we don't want every dropdown to look
   * different! There should be a good common use case for this, but
   * also a use case that we don't want to directly add to the dropdown.
   * Example: Show new dropdown content as part of a `<Tour />`.
   */
  contentContainer?: (props: DropdownContentContainerProps) => ReactNode;
  showToggleIcon?: boolean;
  placement?: Placement;
} & ModeButtonProps;

export const DropdownTrigger: FC<DropdownTriggerProps> = ({
  children,
  'data-testid': dataTestid,
  label,
  active: externalActive,
  initialActive = externalActive ?? false,
  showToggleIcon = true,
  contentContainer = (props) => <div {...props} />,
  renderTrigger = ({ buttonProps }) => <Button {...buttonProps} />,
  placement = 'bottom-end',
  ...buttonProps
}) => {
  const { active, setActive, setWrapperElement, ...dropdown } = useDropdown({
    placement,
    initialActive,
  });

  const toggleActionMode = mapActionModeToButton(useActionMode());
  const actionMode = useMemo(
    () => ({ mode: 'dropdown' }) satisfies ActionMode,
    []
  );

  useLayoutEffect(() => {
    if (externalActive === undefined) return;
    setActive(externalActive);
  }, [externalActive, setActive]);

  const size =
    toggleActionMode && 'size' in toggleActionMode && toggleActionMode.size;

  return (
    <div ref={setWrapperElement}>
      <div
        className={`
          h-full
          legacy:grid
        `}
      >
        {renderTrigger({
          buttonProps: {
            ref: dropdown.refs.setReference,
            className: 'h-full',
            mode: 'icon-inverse',
            ...toggleActionMode,
            ...buttonProps,
            onClick: () => setActive(!active),
            'aria-expanded': active,
            'data-testid': dataTestid,
            children: (
              <span
                className={cn(
                  `
                    grid grid-flow-col items-center gap-xs
                    legacy:gap-[0.5rem]
                  `,
                  size === 'small'
                    ? 'legacy:leading-none'
                    : 'legacy:leading-[1.15]'
                )}
              >
                {!!label && <span>{label}</span>}

                {showToggleIcon ? (
                  <span className="legacy:text-legacy-xs">
                    <FontAwesomeIcon
                      icon={active ? faCaretUp : faCaretDown}
                      aria-hidden="true"
                    />
                  </span>
                ) : null}
              </span>
            ),
          },
        })}
      </div>

      {active &&
        createPortal(
          <div ref={dropdown.refs.setFloating} style={dropdown.style}>
            {contentContainer({
              children: (
                <div
                  data-testid={
                    dataTestid ? `${dataTestid}-dropdown` : 'dropdown-content'
                  }
                  className={cn(
                    dropdownContentClasses,
                    `
                      not-legacy:grid not-legacy:gap-xs not-legacy:rounded-xs
                      not-legacy:px-xs not-legacy:py-sm
                    `
                  )}
                >
                  <ActionModeProvider value={actionMode}>
                    {Children.map(
                      children,
                      function recursive(child): ReactNode {
                        if (isFragment(child)) {
                          return Children.map(child.props.children, recursive);
                        } else if (child) {
                          return (
                            <div
                              className={`
                                legacy:border-b-[0.1rem]
                                legacy:border-b-legacy-brand-light-3
                                legacy:py-[0.9rem]
                                legacy:last:border-b-0
                              `}
                              onClick={() => setActive(false)}
                            >
                              {child}
                            </div>
                          );
                        } else {
                          return null;
                        }
                      }
                    )}
                  </ActionModeProvider>
                </div>
              ),
              className: cn('legacy:rounded-[0.4rem]'),
            })}
          </div>,
          document.body
        )}
    </div>
  );
};
